import { css } from '@emotion/react';
import { AddContactRequest, AuthMethod } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { UsersMutations } from '@frontend/api-users';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, EmailField, PhoneField, useForm, Text, useAlert, ContentLoader } from '@frontend/design-system';
import { MFABackButton } from './mfa-back-button';
import { MFAButtonRow } from './mfa-button-row';
import { useMFAContext } from './mfa-context';

const styles = {
  field: css`
    margin: ${theme.spacing(3)} 0;
  `,
};

export function AddMFAAddress() {
  const { t } = useTranslation('account');
  const alert = useAlert();
  const newVerificationMethod = useMFAContext((ctx) => ctx.newVerificationMethod);
  const fieldType = newVerificationMethod.method;
  const addVerificationMethodAddress = useMFAContext((ctx) => ctx.addVerificationMethodAddress);
  const nextStep = useMFAContext((ctx) => ctx.nextStep);

  const user = getUser();
  const { mutateAsync: addContact, isLoading } = UsersMutations.useAddContact();

  const sendCode = async (contactValue: string) => {
    const payload: AddContactRequest = {
      contact: {
        userId: user?.userID,
        contact: contactValue,
        type: fieldType === 'email' ? AuthMethod.EMAIL : AuthMethod.PHONE,
      },
    };
    try {
      const res = await addContact(payload); // add contact to the user on backend
      if (!res.contactId) {
        throw new Error('No contactId returned from addContact');
      }
      addVerificationMethodAddress(contactValue, res.contactId);
      nextStep();
    } catch (error) {
      if ((error as any)?.status === 400) {
        alert.error(t('That verification method is already in use. Try a different one.'));
      } else {
        alert.error(t('Unexpected error adding method. Please contact support if the problem persists.'));
      }
    }
  };

  const { getFieldProps, formProps, isComplete } = useForm({
    fields: {
      address: {
        type: newVerificationMethod.method === 'email' ? 'email' : 'phone',
        required: true,
        value: newVerificationMethod.address, // this really functions as the defaultValue
      },
    },
    onSubmit(values) {
      if (!values.address) {
        alert.error(t('Please enter a valid address'));
        return;
      }
      sendCode(values.address);
    },
  });

  return (
    <>
      <ContentLoader show={isLoading} />
      <form {...formProps} autoFocus>
        {fieldType === 'email' && (
          <>
            <Text>{t('Enter your email address to receive an authentication code.')}</Text>
            <div css={styles.field}>
              <EmailField {...getFieldProps('address')} label={t('Enter an Email Address')} autoFocus />
            </div>
          </>
        )}
        {fieldType === 'sms' && (
          <>
            <Text>{t('Enter your phone number to receive an authentication code.')}</Text>
            <div css={styles.field}>
              <PhoneField {...getFieldProps('address')} label={t('Enter a Phone Number')} autoFocus />
            </div>
          </>
        )}
        <MFAButtonRow>
          <MFABackButton />
          <Button type='submit' size='large' disabled={!isComplete}>
            {t('Continue')}
          </Button>
        </MFAButtonRow>
      </form>
    </>
  );
}
